import { useCallback, useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { datadogLogs } from '@datadog/browser-logs';
import { showToast } from 'components/Toast';
import { useAppSelector } from 'helpers/hooks';
import { ImpactReportShareMethod, trackEvent } from 'helpers/mixpanelEvents';
import { useShareUrl } from 'hooks/useShareUrl';
import { getUserState } from 'selectors/user';
import { CURRENT_CYCLE_YEAR } from 'screens/Profile/utils';

export type ImpactReportShareStatus = 'idle' | 'generating' | 'ready' | 'failed';
export type ImpactReportShareOutcome =
  | 'shared'
  | 'dismissed'
  | 'downloaded'
  | 'failed'
  | 'unavailable';

export const IMPACT_REPORT_SHARE_TEXT = `Here's my ${CURRENT_CYCLE_YEAR} impact with Oath. Give smarter with me:`;
export const IMPACT_REPORT_SHARE_ERROR =
  "We couldn't create your image. Close the report and try again.";
const FILE_NAME = `oath-${CURRENT_CYCLE_YEAR}-impact-report.png`;
// Instagram Story size. The 390x693 card is drawn into it: 693 is 1/3px short of 390 x 16/9.
const IMAGE_WIDTH = 1080;
const IMAGE_HEIGHT = 1920;

const errorName = (error: unknown) => (error instanceof Error ? error.name : typeof error);

// Resolves with the decode errors, if any.
const waitForImages = async (node: HTMLElement) => {
  const results = await Promise.all(
    Array.from(node.querySelectorAll('img')).map(img =>
      img.decode?.().then(
        () => undefined,
        (error: unknown) => error ?? new Error('Image decode failed')
      )
    )
  );
  return results.filter(result => result !== undefined);
};

const downloadFile = (file: File) => {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const trackShared = (method: ImpactReportShareMethod) =>
  trackEvent('Profile Impact Report Shared', { method, year: CURRENT_CYCLE_YEAR });

export const useImpactReportShare = () => {
  const userState = useAppSelector(getUserState);
  const { baseUrlWithRefCode } = useShareUrl({
    userRefCode: userState.data?.ref_code,
  });
  const [status, setStatus] = useState<ImpactReportShareStatus>('idle');
  const fileRef = useRef<File | null>(null);
  // Bumped by each generate and by cancel, so an older run's result is dropped.
  const runIdRef = useRef(0);

  const generate = useCallback(async (node: HTMLElement) => {
    const runId = ++runIdRef.current;
    const isStale = () => runId !== runIdRef.current;
    setStatus('generating');
    fileRef.current = null;
    try {
      await document.fonts?.ready;
      const imageErrors = await waitForImages(node);
      if (isStale()) return;
      // html-to-image drops an image it can't fetch without failing, so log it here.
      imageErrors.forEach(error =>
        datadogLogs.logger.warn('Impact report image asset failed to load', {
          error_name: errorName(error),
        })
      );
      const blob = await toBlob(node, {
        canvasWidth: IMAGE_WIDTH,
        canvasHeight: IMAGE_HEIGHT,
        pixelRatio: 1,
      });
      if (isStale()) return;
      if (!blob) throw new Error('Empty image');
      fileRef.current = new File([blob], FILE_NAME, { type: 'image/png' });
      setStatus('ready');
    } catch (error) {
      if (isStale()) return;
      datadogLogs.logger.warn('Impact report image generation failed', {
        error_name: errorName(error),
      });
      setStatus('failed');
    }
  }, []);

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    fileRef.current = null;
    setStatus('idle');
  }, []);

  const nativeShare = (
    data: ShareData,
    method: ImpactReportShareMethod
  ): Promise<ImpactReportShareOutcome> =>
    navigator
      .share(data)
      .then(() => {
        trackShared(method);
        return 'shared' as const;
      })
      .catch(error => {
        // The user closed the share sheet.
        if (errorName(error) === 'AbortError') return 'dismissed';
        datadogLogs.logger.warn('Impact report share failed', { error_name: errorName(error) });
        return 'failed';
      });

  // Must call navigator.share synchronously: iOS drops the tap's user activation across an await.
  const share = (): Promise<ImpactReportShareOutcome> => {
    if (status !== 'ready' && status !== 'failed') return Promise.resolve('unavailable');

    // Not handleShare's URL: that can be a stale cart URL left in the store.
    const linkData = { text: IMPACT_REPORT_SHARE_TEXT, url: baseUrlWithRefCode };
    const file = fileRef.current;
    if (!file) {
      // Image failed: share the message and link where possible.
      if (typeof navigator.share === 'function') return nativeShare(linkData, 'link_only');
      showToast(IMPACT_REPORT_SHARE_ERROR, 'error');
      return Promise.resolve('failed');
    }

    const data = { ...linkData, files: [file] };
    if (navigator.canShare?.(data)) return nativeShare(data, 'share_sheet');
    downloadFile(file);
    trackShared('download');
    return Promise.resolve('downloaded');
  };

  return { generate, cancel, share, status };
};
